import {
  ActionReducerMapBuilder,
  createAction,
  EntityState,
  createAsyncThunk,
} from '@reduxjs/toolkit';
import { classToPlain } from 'class-transformer';
import { FORM_ERROR } from 'final-form';

import { AppError } from '@vk-hr-tek/core/error';
import { Filter } from '@vk-hr-tek/core/filter';
import { showCustomNotification } from '@vk-hr-tek/core/notifications';

import {
  AttorneyImportRequest,
  AttorneyUser,
  UsersService,
} from '@app/gen/attorneys';
import { ThunkExtra } from '@app/store';
import { CompanyEventsStat, CompanyGroupUsers } from '@app/gen/settings';
import { Entity } from '@app/types';

import { AttorneysUsersService } from '../../../services/attorneys';
import { SettingsState, SettingsWithRootState } from '../../settings.state';
import { CreateAttorneyDto, GetAttorneysUsersDto } from '../../../dto';

const setFilters = createAction<Record<string, Filter[]>>(
  'settings/attorneys/setFilters',
);

export const getUsers = createAsyncThunk<
  { data: AttorneyUser[]; total: number },
  GetAttorneysUsersDto,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/attorneys/getUsers',
  async (
    getAttorneysUsersDto,
    { rejectWithValue, getState, dispatch, extra: { inject } },
  ) => {
    try {
      const state = getState().settings;
      const service = inject(UsersService);
      const attorneysUsersService = inject(AttorneysUsersService);

      let filters = state.attorneysUsers.filters;
      const companies = state.attorneysCompanies.items;

      const companyId = getAttorneysUsersDto.company_id;

      if (
        !companies ||
        !companies.find((company) => company.company.id === companyId)
      ) {
        const error = new AppError('client', {
          code: 404,
          message: 'Компания не найдена',
        });

        error.title = 'Компания не найдена';

        throw error;
      }

      if (!filters[companyId]) {
        const filtersResponse = await service.attorneyUserListAvailableFilters({
          query: { company_id: companyId },
        });

        filters = { [companyId]: filtersResponse.filters };
        dispatch(setFilters(filters));
      }

      return await attorneysUsersService.get(
        getAttorneysUsersDto,
        filters[companyId],
      );
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const importAttorney = createAsyncThunk<
  void,
  {
    values: {
      importAttorneyDto: AttorneyImportRequest;
      employeeName: string;
      companyName: string;
    };
    actions: {
      resolve: (value?: unknown) => void;
      reject: (value?: unknown) => void;
    };
  },
  ThunkExtra<SettingsWithRootState>
>(
  'settings/attorneys/importAttorney',
  async (
    { values: { importAttorneyDto, employeeName, companyName }, actions },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    try {
      const service = inject(UsersService);

      await service.attorneyImport({
        body: importAttorneyDto,
      });

      actions.resolve();

      dispatch(
        showCustomNotification([
          {
            type: 'info' as const,
            title: 'МЧД успешно импортирована!',
            subtitle: companyName,
            message: employeeName,
          },
        ]),
      );
    } catch (err) {
      actions.reject({ [FORM_ERROR]: err });
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const createAttorney = createAsyncThunk<
  void,
  {
    values: {
      createAttorneyDto: CreateAttorneyDto;
      employeeName: string;
      companyName: string;
    };
    actions: {
      resolve: (value?: unknown) => void;
      reject: (value?: unknown) => void;
    };
  },
  ThunkExtra<SettingsWithRootState>
>(
  'settings/attorneys/createAttorney',
  async (
    { values: { createAttorneyDto, employeeName, companyName }, actions },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    try {
      const service = inject(UsersService);

      await service.attorneysCreate({
        body: createAttorneyDto,
      });

      actions.resolve();

      dispatch(
        showCustomNotification([
          {
            type: 'info' as const,
            title: 'МЧД Создана!',
            subtitle: companyName,
            message: employeeName,
          },
        ]),
      );
    } catch (err) {
      actions.reject({ [FORM_ERROR]: err });
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const settingsAttorneysCompaniesReducers = (
  builder: ActionReducerMapBuilder<
    EntityState<Entity<CompanyEventsStat | CompanyGroupUsers>, string> &
      SettingsState
  >,
) => {
  builder.addCase(setFilters, (state, action) => {
    state.attorneysUsers.filters = {
      ...state.attorneysUsers.filters,
      ...action.payload,
    };
  });
  builder.addCase(getUsers.pending, (state) => {
    state.attorneysUsers.status = 'loading';
    state.attorneysUsers.error = null;
  });
  builder.addCase(getUsers.fulfilled, (state, { payload }) => {
    state.attorneysUsers.status = 'complete';
    state.attorneysUsers.error = null;
    state.attorneysUsers.items = payload.data;
    state.attorneysUsers.total = payload.total;
  });
  builder.addCase(getUsers.rejected, (state, { payload, error }) => {
    state.attorneysUsers.status = 'failed';
    state.attorneysUsers.error =
      payload ||
      ({
        info: (error && error.message) || 'Unknown error',
        status: 500,
        source: 'client',
        title: 'Internal client error',
      } as AppError);
  });
};
